// src/components/ReviewsManager.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import './AdminDashboard.css';
import './AdminDashboardModern.css';

const ReviewsManager = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/reviews/v1/get-review?site_id=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviews(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (review) => {
    setReviewToDelete(review);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reviewToDelete) return;
    
    setDeleting(reviewToDelete.id);
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/reviews/v1/delete-review?site_id=${SITE_CONFIG.SITE_ID}&id=${reviewToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        fetchReviews();
        setShowDeleteModal(false);
        setReviewToDelete(null);
      } else {
        alert('Failed to delete review');
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Failed to delete review');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="admin-dashboard-modern">
      {/* Header */}
      <header className="admin-header-modern">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">Review Management</h1>
            <p className="dashboard-subtitle">Monitor and manage customer reviews and testimonials</p>
          </div>
          <div className="header-right">
            <button onClick={() => navigate('/admin')} className="back-home-btn-modern">
              <span className="btn-icon">←</span>
              Back to Admin
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main-content">
        {/* Stats Section */}
        <div className="stats-section">
          <div className="stat-card-modern">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <div className="stat-number">{reviews.length}</div>
              <div className="stat-label">Total Reviews</div>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-number">5.0</div>
              <div className="stat-label">Average Rating</div>
            </div>
          </div>
          <div className="stat-card-modern">
            <div className="stat-icon">📈</div>
            <div className="stat-content">
              <div className="stat-number">{reviews.filter(r => new Date(r.created_date) > new Date(Date.now() - 30*24*60*60*1000)).length}</div>
              <div className="stat-label">This Month</div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="reviews-section">
          <div className="section-header">
            <h2 className="section-title">Customer Reviews</h2>
          </div>
          
          <div className="reviews-grid-modern">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading reviews...</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">⭐</div>
                <h3>No reviews yet</h3>
                <p>Customer reviews and testimonials will appear here</p>
              </div>
            ) : (
              reviews.map((review, index) => (
                <div key={review.id || index} className="review-card-modern">
                  <div className="review-header">
                    <div className="review-rating">
                      <div className="stars">⭐⭐⭐⭐⭐</div>
                      <span className="rating-text">5.0</span>
                    </div>
                    <div className="review-actions">
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteClick(review)}
                        disabled={deleting === review.id}
                        title="Delete review"
                      >
                        {deleting === review.id ? '⏳' : '🗑️'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="review-content">
                    <p className="review-text">{review.review_text}</p>
                  </div>
                  
                  <div className="review-footer">
                    <div className="review-meta">
                      <span className="review-date">
                        {new Date(review.created_date || Date.now()).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      <span className="review-id">#{review.id}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
      
      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Review</h3>
              <button 
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <p className="modal-text">Are you sure you want to delete this review? This action cannot be undone.</p>
              
              <div className="review-preview-modern">
                <div className="preview-header">
                  <div className="preview-stars">⭐⭐⭐⭐⭐</div>
                </div>
                <p className="preview-text">"{reviewToDelete?.review_text}"</p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteConfirm}
                className="btn-danger"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsManager;